import * as React from "react";
import { format } from "date-fns";
import { Button } from "./ui/button";
import { Loader } from "lucide-react";
const Dialog = React.lazy(() => import("./ui/dialog").then(module => ({ default: module.Dialog })))
const DialogContent = React.lazy(() => import("./ui/dialog").then(module => ({ default: module.DialogContent })))
const DialogHeader = React.lazy(() => import("./ui/dialog").then(module => ({ default: module.DialogHeader })))
const DialogFooter = React.lazy(() => import("./ui/dialog").then(module => ({ default: module.DialogFooter })))
const DialogTitle = React.lazy(() => import("./ui/dialog").then(module => ({ default: module.DialogTitle })))
const DialogDescription = React.lazy(() => import("./ui/dialog").then(module => ({ default: module.DialogDescription })))
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Pencil, Trash2 } from "lucide-react";
import { Chat } from "../../types/research.types";
import { useUpdateChat } from "../../hooks/useUpdateChat";
import { useDeleteChat } from "../../hooks/useDeleteChat";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";

interface ResearchChatListProps {
  chats: Chat[];
  searchQuery: string;
  onChatClick: (chatId: string) => void;
}

const ResearchChatList: React.FC<ResearchChatListProps> = ({ chats, searchQuery, onChatClick }) => {
  const updateChatMutation = useUpdateChat();
  const deleteChatMutation = useDeleteChat();

  const [editingChatId, setEditingChatId] = React.useState<string | null>(null);
  const [editingChatTitle, setEditingChatTitle] = React.useState("");
  const [deletingChatId, setDeletingChatId] = React.useState<string | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = React.useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);

  const handleEditClick = (chatId: string, currentTitle: string) => {
    setEditingChatId(chatId);
    setEditingChatTitle(currentTitle);
    setIsEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (editingChatId && editingChatTitle.trim()) {
      await updateChatMutation.mutateAsync({
        chat_id: editingChatId,
        new_name: editingChatTitle.trim(),
      });
      setIsEditDialogOpen(false);
      setEditingChatId(null);
      setEditingChatTitle("");
    }
  };

  const handleEditCancel = () => {
    setIsEditDialogOpen(false);
    setEditingChatId(null);
    setEditingChatTitle("");
  };

  const handleDeleteClick = (chatId: string) => {
    setDeletingChatId(chatId);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (deletingChatId) {
      await deleteChatMutation.mutateAsync(deletingChatId);
      setIsDeleteDialogOpen(false);
      setDeletingChatId(null);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteDialogOpen(false);
    setDeletingChatId(null);
  };

  return (
    <>
      {/* Chat List */}
      <div className="flex-1 overflow-auto flex flex-col gap-2">
        {chats.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-8 text-center">
            <p className="text-sm text-neutral-400">
              {searchQuery ? "No chats found matching your search." : "No chats available yet."}
            </p>
          </div>
        ) : (
          chats.map((chat) => (
            <TooltipProvider key={chat.chat_id}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className="flex items-center justify-between px-4 py-3.5 bg-primary-100 rounded-lg  transition-all cursor-pointer "
                    onClick={() => onChatClick(chat.chat_id)}
                  >
                    <div
                      className="flex flex-col gap-1 flex-1 min-w-0"
                    >
                      <p className="text-sm font-medium text-neutral-400 truncate text-left">
                        {chat.chat_name}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 ml-2">
                      <p className="text-xs text-neutral-500">
                        {format(new Date(chat.creation_date), "do MMM, yyyy")}
                      </p>
                      <Button
                        variant="ghost"
                        className="w-auto h-auto p-0"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditClick(chat.chat_id, chat.chat_name);
                        }}
                        disabled={updateChatMutation.isPending || deleteChatMutation.isPending}
                      >
                        <Pencil className="size-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        className="w-auto h-auto p-0"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(chat.chat_id);
                        }}
                        disabled={updateChatMutation.isPending || deleteChatMutation.isPending}
                      >
                        <Trash2 className="size-3.5 text-danger-200" />
                      </Button>
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="max-w-[260px]">
                  <p>{chat.chat_name}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ))
        )}
      </div>

      {/* Edit Dialog */}
      <React.Suspense fallback={<Loader className="animate-spin" />}>
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="p-4">
            <DialogHeader className="sm:text-center">
              <DialogTitle className="text-base font-medium text-center">Edit Chat Title</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="chatTitle" className="font-medium text-sm leading-5 text-neutral-400">Chat Title</Label>
                <Input
                  id="chatTitle"
                  value={editingChatTitle}
                  onChange={(e) => setEditingChatTitle(e.target.value)}
                  placeholder="Enter chat title"
                  className="placeholder:text-neutral-500 text-sm py-3 px-4 h-auto"
                />
              </div>
            </div>
            <DialogFooter className="flex-row gap-2 sm:justify-center">
              <Button
                variant="secondary"
                onClick={handleEditCancel}
                disabled={updateChatMutation.isPending}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={handleEditSave}
                disabled={!editingChatTitle.trim() || updateChatMutation.isPending}
                className="flex-1"
              >
                {updateChatMutation.isPending ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </React.Suspense>

      {/* Delete Confirmation Dialog */}
      <React.Suspense fallback={<Loader className="animate-spin" />}>
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent className="p-4">
            <DialogHeader className="sm:text-center">
              <DialogTitle className="text-base font-medium text-center">Delete Chat</DialogTitle>
              <DialogDescription className="text-center">
                Are you sure you want to delete this chat? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex-row gap-2 sm:justify-center">
              <Button
                variant="secondary"
                onClick={handleDeleteCancel}
                disabled={deleteChatMutation.isPending}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteConfirm}
                disabled={deleteChatMutation.isPending}
                className="flex-1"
              >
                {deleteChatMutation.isPending ? "Deleting..." : "Delete"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </React.Suspense>
    </>
  );
};

export default ResearchChatList;
